import { z } from 'zod'
import { expandSeries, recurrenceRuleSchema, type RecurrenceRule } from './recurrence.ts'
import type { Blockout } from './scheduling.ts'

/**
 * A plan's times — its services, rehearsals and anything else people are
 * called for — and what hangs off them: which times someone serves, where
 * the running order's clock starts for each service, and blockouts that
 * repeat.
 *
 * Times are local: a date and a wall time in the church's zone, like the plan
 * itself, because "the 9:00" is 9:00 whatever the clocks did that weekend.
 */

export const PLAN_TIME_KINDS = ['service', 'rehearsal', 'other'] as const
export type PlanTimeKind = (typeof PLAN_TIME_KINDS)[number]

export const PLAN_TIME_KIND_LABELS: Record<PlanTimeKind, string> = {
  service: 'Service',
  rehearsal: 'Rehearsal',
  other: 'Other',
}

export interface PlanTimeLike {
  id: string
  kind: string
  name: string | null
  date: string
  startTime: string
  endTime: string | null
}

const KIND_ORDER: Record<string, number> = { service: 0, rehearsal: 1, other: 2 }

/** In the order they happen: by day, then start, services before the rest at the same minute. */
export function sortPlanTimes<T extends PlanTimeLike>(times: readonly T[]): T[] {
  return [...times].sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      a.startTime.localeCompare(b.startTime) ||
      (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9) ||
      a.id.localeCompare(b.id),
  )
}

/** The earliest service: the plan's own date and start time. */
export function firstServiceTime<T extends PlanTimeLike>(times: readonly T[]): T | undefined {
  return sortPlanTimes(times.filter((time) => time.kind === 'service'))[0]
}

/** "9:00" when it has a name; else "Service 09:00", "Rehearsal 19:30". */
export function planTimeLabel(time: Pick<PlanTimeLike, 'kind' | 'name' | 'startTime'>): string {
  if (time.name) return time.name
  const kind = PLAN_TIME_KIND_LABELS[time.kind as PlanTimeKind] ?? 'Time'
  return `${kind} ${time.startTime}`
}

/** "09:00" plus an offset in seconds — negative for before it — as a wall time. */
export function clockAt(startTime: string, offsetSeconds: number): string {
  const [h = 0, m = 0] = startTime.split(':').map(Number)
  const day = 24 * 3600
  const total = (((h * 3600 + m * 60 + offsetSeconds) % day) + day) % day
  const hh = Math.floor(total / 3600)
  const mm = Math.floor((total % 3600) / 60)
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

/** A local date moved by whole days. */
/** A clock time as a list reads it: "19:30" → "7:30pm", "09:00" → "9:00am". */
export function shortClock(time: string): string {
  const [h = '0', m = '00'] = time.split(':')
  const hour = Number(h)
  return `${hour % 12 === 0 ? 12 : hour % 12}:${m.padStart(2, '0')}${hour < 12 ? 'am' : 'pm'}`
}

/** A date as month/day: "2026-10-25" → "10/25". */
export function monthDay(date: string): string {
  const [, month = '1', day = '1'] = date.split('-')
  return `${Number(month)}/${Number(day)}`
}

/** A plan time's start and day, as its row shows it: "7:00am 10/25". */
export function planTimeWhen(time: Pick<PlanTimeLike, 'startTime'> & { date: string }): string {
  return `${shortClock(time.startTime)} ${monthDay(time.date)}`
}

export function shiftDate(date: string, days: number): string {
  return new Date(new Date(`${date}T00:00:00Z`).getTime() + days * 86_400_000)
    .toISOString()
    .slice(0, 10)
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  return Math.round(
    (new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86_400_000,
  )
}

/**
 * The times someone serves, from what they are excused: every time not
 * excluded. Stored the other way round so a time added later is theirs too.
 */
export function servedTimeIds(
  times: readonly { id: string }[],
  excluded: readonly string[],
): string[] {
  const out = new Set(excluded)
  return times.filter((time) => !out.has(time.id)).map((time) => time.id)
}

/** What to store for a chosen set of times: the plan's times they do not serve. */
export function excludedTimeIds(
  times: readonly { id: string }[],
  served: readonly string[],
): string[] {
  const chosen = new Set(served)
  return times.filter((time) => !chosen.has(time.id)).map((time) => time.id)
}

/** The days someone is needed on, for the times they serve. */
export function datesServed(
  times: readonly { id: string; date: string }[],
  excluded: readonly string[],
): string[] {
  const out = new Set(excluded)
  return [...new Set(times.filter((t) => !out.has(t.id)).map((t) => t.date))].sort()
}

export const planTimeSchema = z
  .object({
    kind: z.enum(PLAN_TIME_KINDS),
    name: z.string().trim().max(60).nullish(),
    date: z.iso.date(),
    startTime: z.string().regex(/^[0-2][0-9]:[0-5][0-9]$/),
    endTime: z
      .string()
      .regex(/^[0-2][0-9]:[0-5][0-9]$/)
      .nullish(),
  })
  .refine((t) => !t.endTime || t.endTime > t.startTime, {
    message: 'It ends before it starts.',
    path: ['endTime'],
  })

// ---- before, during and after the service -------------------------------------

/**
 * An item's phase: before the service (a countdown, the walk-in loop), during
 * it, or after it (PCO's `service_position`). The phase is for time keeping
 * and reporting: the service's length and its printouts are During alone.
 * The running order shows every item, in its order; a Before or After item
 * carries a small muted mark, not a band of its own.
 */
export const SERVICE_POSITIONS = ['pre', 'during', 'post'] as const
export type ServicePosition = (typeof SERVICE_POSITIONS)[number]

export const SERVICE_POSITION_LABELS: Record<ServicePosition, string> = {
  pre: 'Before',
  during: 'During',
  post: 'After',
}

/** A stored phase, defensively: anything not Before or After is During. */
export function servicePositionOf(item: { servicePosition?: string }): ServicePosition {
  return item.servicePosition === 'pre' || item.servicePosition === 'post'
    ? item.servicePosition
    : 'during'
}

/** The mark a Before or After item wears in the order ("Before"); null for During. */
export function servicePositionMark(item: { servicePosition?: string }): string | null {
  const position = servicePositionOf(item)
  return position === 'during' ? null : SERVICE_POSITION_LABELS[position]
}

const POSITION_ORDER: Record<ServicePosition, number> = { pre: 0, during: 1, post: 2 }

/** Before items first, then During, then After — each in its own order. */
export function byServicePosition<T extends { servicePosition?: string }>(
  items: readonly T[],
): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        POSITION_ORDER[servicePositionOf(a.item)] - POSITION_ORDER[servicePositionOf(b.item)] ||
        a.index - b.index,
    )
    .map(({ item }) => item)
}

/**
 * What a printout or an export of a running order shows: the service itself,
 * During items (and headers) in their order. Before and After items stay in
 * the app — the order, the Music Stand — but not on paper.
 */
export function printableItems<T extends { servicePosition?: string }>(items: readonly T[]): T[] {
  return items.filter((item) => servicePositionOf(item) === 'during')
}

// ---- blockouts that repeat ----------------------------------------------------

/**
 * A blockout's repeat: the existing rule shape (weekly, every other week, the
 * first Sunday…) with an end. Yearly is monthly every twelve.
 */
export const blockoutRuleSchema = recurrenceRuleSchema

export interface StoredBlockout {
  personId: string
  startsOn: string
  endsOn: string
  rule: RecurrenceRule | null
}

/** A stored rule, or null if it is not one (never trust a jsonb column blindly). */
export function parseBlockoutRule(value: unknown): RecurrenceRule | null {
  if (value === null || value === undefined) return null
  const parsed = blockoutRuleSchema.safeParse(value)
  return parsed.success ? (parsed.data as RecurrenceRule) : null
}

/**
 * Every time a blockout is away that touches the window `from`–`to`
 * (inclusive local dates), as plain ranges the conflict checks read. A
 * blockout that does not repeat is itself; one that does comes round as
 * often as its rule says, each time as long as the first.
 */
export function expandBlockouts(
  blockouts: readonly StoredBlockout[],
  from: string,
  to: string,
): Blockout[] {
  const out: Blockout[] = []
  for (const blockout of blockouts) {
    const span = daysBetween(blockout.startsOn, blockout.endsOn)
    if (!blockout.rule) {
      if (blockout.startsOn <= to && blockout.endsOn >= from)
        out.push({
          personId: blockout.personId,
          startsOn: blockout.startsOn,
          endsOn: blockout.endsOn,
        })
      continue
    }
    // Wall time in UTC: only the dates matter, and UTC has no DST to skip.
    const occurrences = expandSeries(
      {
        start: `${blockout.startsOn}T00:00`,
        durationMinutes: 5,
        timeZone: 'UTC',
        rule: blockout.rule,
      },
      {
        from: new Date(`${shiftDate(from, -span)}T00:00:00Z`),
        to: new Date(`${shiftDate(to, 1)}T00:00:00Z`),
        limit: 2000,
      },
    )
    for (const occurrence of occurrences) {
      const startsOn = occurrence.localStart.slice(0, 10)
      out.push({ personId: blockout.personId, startsOn, endsOn: shiftDate(startsOn, span) })
    }
  }
  return out
}

/** "Every week until 31 Dec", for a blockout's line on My Schedule. */
export function describeBlockoutRule(rule: RecurrenceRule): string {
  const interval = Math.max(1, rule.interval ?? 1)
  const base =
    rule.freq === 'MONTHLY' && interval === 12
      ? 'Every year'
      : rule.freq === 'MONTHLY'
        ? interval === 1
          ? 'Every month'
          : `Every ${interval} months`
        : rule.freq === 'WEEKLY'
          ? interval === 1
            ? 'Every week'
            : interval === 2
              ? 'Every other week'
              : `Every ${interval} weeks`
          : interval === 1
            ? 'Every day'
            : `Every ${interval} days`
  return rule.until ? `${base} until ${rule.until.slice(0, 10)}` : base
}

/** The repeats My Schedule offers, as rules. */
export const BLOCKOUT_REPEATS = {
  weekly: { freq: 'WEEKLY', interval: 1 },
  fortnightly: { freq: 'WEEKLY', interval: 2 },
  monthly: { freq: 'MONTHLY', interval: 1 },
  yearly: { freq: 'MONTHLY', interval: 12 },
} as const satisfies Record<string, RecurrenceRule>
export type BlockoutRepeat = keyof typeof BLOCKOUT_REPEATS

export const BLOCKOUT_REPEAT_LABELS: Record<BlockoutRepeat, string> = {
  weekly: 'Every week',
  fortnightly: 'Every other week',
  monthly: 'Every month',
  yearly: 'Every year',
}

/** Which of the offered repeats a rule is, if it is one of them. */
export function blockoutRepeatOf(rule: RecurrenceRule | null): BlockoutRepeat | null {
  if (!rule) return null
  for (const [key, value] of Object.entries(BLOCKOUT_REPEATS))
    if (value.freq === rule.freq && value.interval === Math.max(1, rule.interval ?? 1))
      return key as BlockoutRepeat
  return null
}

// ---- categorised notes ---------------------------------------------------------

/** A service type's note categories, tidied: trimmed, no blanks, no repeats, at most 12. */
export function normaliseNoteCategories(categories: readonly string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of categories) {
    const name = raw.trim().replace(/\s+/g, ' ').slice(0, 40)
    if (!name || seen.has(name.toLowerCase())) continue
    seen.add(name.toLowerCase())
    out.push(name)
  }
  return out.slice(0, 12)
}

/**
 * Merges a change into stored category notes: a category with text sets it,
 * one with empty text (or null) clears it. Notes are kept for a category the
 * type no longer has — renaming a category should not lose what was written.
 */
export function mergeCategoryNotes(
  current: Readonly<Record<string, string>>,
  change: Readonly<Record<string, string | null>>,
): Record<string, string> {
  const next: Record<string, string> = { ...current }
  for (const [category, text] of Object.entries(change)) {
    const value = text?.trim() ?? ''
    if (value) next[category] = value.slice(0, 2000)
    else delete next[category]
  }
  return next
}

/** Stored category notes, defensively: only string values survive. */
export function parseCategoryNotes(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1] !== '',
    ),
  )
}

/** Category notes in the type's order, then any for categories it no longer has. */
export function orderedCategoryNotes(
  notes: Readonly<Record<string, string>>,
  categories: readonly string[],
): { category: string; text: string }[] {
  const known = categories
    .filter((c) => notes[c])
    .map((c) => ({ category: c, text: notes[c] ?? '' }))
  const rest = Object.keys(notes)
    .filter((c) => !categories.includes(c))
    .sort()
    .map((c) => ({ category: c, text: notes[c] ?? '' }))
  return [...known, ...rest]
}
